import React from 'react';
import type { BirdEyeBoardCounts, BirdEyeFinishedSummary, BirdEyeKpiExtras } from '../../bird-eye/boardTypes';
import type { BirdEyeKpiStat } from '../../bird-eye/types';
import { formatMoney } from '../../bird-eye/utils';
import { Icon } from '../Icon/Icon';

function buildStats(
  counts: BirdEyeBoardCounts,
  finishedSummary?: BirdEyeFinishedSummary,
  extras?: BirdEyeKpiExtras,
): BirdEyeKpiStat[] {
  const finished = finishedSummary ?? { total: 0, production: 0 };
  const scheduledToday =
    extras?.scheduledToday ??
    counts.arriving + counts.waiting + counts.walking + counts.seated + finished.total;
  const avgWait = extras?.avgWaitMinutes;
  return [
    { label: 'Scheduled today', value: scheduledToday, icon: 'calendar', accent: 'var(--ink-2)', sub: 'across all lanes' },
    {
      label: 'Arriving soon',
      value: counts.arriving,
      icon: 'phone-in',
      accent: 'var(--ai-4)',
      sub: 'yet to arrive',
    },
    {
      label: 'Waiting room',
      value: counts.waiting,
      icon: 'clock',
      accent: 'var(--gold)',
      sub: avgWait != null ? `avg wait ${String(avgWait)}m` : 'no one waiting',
    },
    {
      label: 'In operatory',
      value: `${String(counts.seated)}/${String(counts.opTotal)}`,
      icon: 'pulse',
      accent: 'var(--ai-3)',
      sub: 'rooms seated',
    },
    {
      label: 'Walking out',
      value: counts.walking,
      icon: 'arrow-r',
      accent: 'var(--ai-5)',
      sub: 'to checkout',
    },
    {
      label: 'Finished',
      value: finished.total,
      icon: 'check-circ',
      accent: 'var(--success)',
      sub: `${formatMoney(finished.production)} captured`,
    },
  ];
}

export interface BirdEyeKpiStripProps {
  counts: BirdEyeBoardCounts;
  finishedSummary?: BirdEyeFinishedSummary;
  extras?: BirdEyeKpiExtras;
}

export function BirdEyeKpiStrip({ counts, finishedSummary, extras }: BirdEyeKpiStripProps) {
  const stats = buildStats(counts, finishedSummary, extras);

  return (
    <div className="mb-[18px] grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[17px] py-[15px] shadow-[var(--sh-1)]"
        >
          <div className="mb-3 flex items-center gap-[9px]">
            <span
              className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] border border-[var(--cream-2)] bg-[var(--cream-0)] text-[var(--accent)]"
              style={{ color: s.accent }}
            >
              <Icon kind={s.icon} size={14} stroke={1.8} />
            </span>
            <span className="font-[var(--font-mono)] text-[10px] font-bold uppercase leading-[1.3] tracking-[0.13em] text-[var(--ink-3)]">
              {s.label}
            </span>
            {s.delta ? (
              <span
                className="ml-auto inline-flex items-center gap-0.5 font-[var(--font-mono)] text-[11px] font-semibold"
                style={{ color: s.up ? 'var(--success)' : 'var(--terracotta)' }}
              >
                <Icon kind={s.up ? 'up' : 'down'} size={11} stroke={2.6} />
                {s.delta}
                <span className="font-medium text-[var(--ink-4)]">avg</span>
              </span>
            ) : null}
          </div>
          <div
            className="font-[var(--font-display)] text-[40px] font-semibold leading-[0.95] tracking-[-0.04em] tabular-nums"
            style={{
              color:
                s.accent === 'var(--success)' || s.accent === 'var(--gold)' ? s.accent : 'var(--ink-0)',
            }}
          >
            {s.value}
          </div>
          <div className="mt-2.5 text-[11.5px] text-[var(--ink-3)]">{s.sub}</div>
        </div>
      ))}
    </div>
  );
}
